import { StringHelpers } from '@mode/shared/util-js';
import * as angular from 'angular';
import _ from 'lodash';
import { ModelAccessorBase, ModelAccessorInjectables } from '../model_accessor';
import { ModeModelBase } from '../model';
import { Injectable } from '@angular/core';

const weekArray = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const buildCreator = (schedule) => {
  return schedule
    .creator()
    .then((creator) => {
      schedule.savedCreator = creator;
      return schedule;
    })
    .catch((e) => {
      return schedule;
    });
};

export class ReportScheduleModel extends ModeModelBase {
  override _name = 'reportSchedule';
  frequencyText: string;
  fullFrequencyText: string;
  fullParamsText: string | any[];
  frequency: any;
  cron: any;
  minute: string;
  name: string;
  hour: string;
  day_of_week: string;
  day_of_month: any;
  time_zone: any;
  params: any;
  override idAttribute = 'token';
  override belongsTo = 'report';
  override parentIdAttribute = 'token';
  override hasMany = [{ name: 'reportRuns', idAttribute: 'token' }, { name: 'reportSubscriptions' }];
  override hasOne = [{ name: 'account', as: 'creator', idAttribute: 'username' }];
  override attributes = [
    { name: 'created_at', default: null },
    { name: 'updated_at', default: null },
    { name: 'last_run_at', default: null },
    {
      name: 'cron',
      default: {
        day_of_week: 1,
        day_of_month: 1,
        hour: 9,
      },
    },
    { name: 'name', default: '' },
    { name: 'params', default: null, preserveCase: true },
    { name: 'parameters', default: null, preserveCase: true },
    { name: 'frequency', default: null },
    { name: 'hour', default: null },
    { name: 'minute', default: null },
    { name: 'time_zone', default: null },
    { name: 'day_of_month', default: null },
    { name: 'day_of_week', default: null },
  ];
  override initialize(resource) {
    if (resource && resource.hasForm('edit')) {
      _.merge(this, resource.forms('edit').$serialize().report_schedule);
    }
    if (resource) {
      this.name = resource.name;
      this.frequencyText = this.getFrequencyText();
      this.fullFrequencyText = this.getFullFrequencyText();
      this.fullParamsText = this.getFullParamsTextArray();
    }
  }
  updateFromCron() {
    this.frequency = this.cron.freq;
    this.minute = this.minuteText(this.cron.minute);
    this.hour = this.hourNameFromCron() + this.minuteNameFromCron();
    this.day_of_week = this.dayOfWeekText(this.cron.day_of_week);
    this.day_of_month = this.dayOfMonthText(this.cron.day_of_month);
    this.time_zone = this.cron.time_zone;
  }
  hourNameFromCron() {
    return this.cron ? (this.cron.hour % 12 ? this.cron.hour % 12 : 12) : '';
  }
  minuteNameFromCron() {
    const displayTime = (time, period) => {
      return time < 10 ? ':0' + time + ' ' + period : ':' + time + ' ' + period;
    };
    return this.cron
      ? this.cron.hour < 12
        ? displayTime(this.cron.minute, 'am')
        : displayTime(this.cron.minute, 'pm')
      : '';
  }
  getFrequencyText() {
    let text = '';
    switch (this.frequency) {
      case 'every_15_minutes':
        text = 'Every 15 minutes';
        if (this.minute) {
          text += ' starting at ' + this.minute;
        }
        break;
      case 'every_30_minutes':
        text = 'Every 30 minutes';
        if (this.minute) {
          text += ' starting at ' + this.minute;
        }
        break;
      case 'hourly':
        text = 'Every hour';
        if (this.minute) {
          text += ' at ' + this.minute;
        }
        break;
      case 'daily':
        text = 'Daily at ' + this.hour + ' ' + this.time_zone;
        break;
      case 'weekly':
        text = 'Every ' + this.day_of_week + ' at ' + this.hour + ' ' + this.time_zone;
        break;
      case 'monthly':
        text = 'Every ' + this.day_of_month + ' of the month at ' + this.hour + ' ' + this.time_zone;
        break;
    }
    return text;
  }
  minuteText(minute) {
    if (minute === 0) {
      return 'the top of the hour';
    } else {
      return minute + ' minutes past the hour';
    }
  }
  dayOfWeekText(day) {
    if (typeof day === 'number' && weekArray[day]) {
      return weekArray[day];
    } else {
      return '';
    }
  }
  dayOfMonthText(day) {
    return StringHelpers.ordinal(day);
  }
  getFullFrequencyText() {
    let text = this.getFrequencyText();
    text = text.charAt(0).toLowerCase() + text.slice(1);
    return 'Runs ' + text;
  }
  getFullParamsTextArray() {
    if (this.params) {
      const paramsTextArray = [];

      angular.forEach(
        Object.keys(this.params),
        (paramName) => {
          paramsTextArray.push(paramName + ' = ' + this.params[paramName]);
        },
        this
      );

      return paramsTextArray;
    }
    return '';
  }
  getMinuteRange() {
    if (!this.cron || !this.cron.freq) {
      return undefined; // indicates no range aka all minutes
    }
    if (this.cron.freq === 'every_15_minutes') {
      return 3;
    } else if (this.cron.freq === 'every_30_minutes') {
      return 6;
    } else {
      return undefined; // indicates no range aka all minutes
    }
  }
  resetMinuteRange() {
    if (this.cron.freq === 'every_15_minutes' && this.cron.minute >= 15) {
      this.cron.minute = 0;
    } else if (this.cron.freq === 'every_30_minutes' && this.cron.minute >= 30) {
      this.cron.minute = 0;
    }
  }
}
@Injectable({ providedIn: 'root' })
export class ReportScheduleModelAccessor extends ModelAccessorBase<ReportScheduleModel> {
  override name = 'reportSchedule';
  override idAttribute = 'token';
  override parentModel = 'report';
  override parentIdAttribute = 'token';
  override createAttributes = ['report'];
  constructor(injectables: ModelAccessorInjectables) {
    super(injectables, 'migrated', 'mode.reportSchedule');
  }
  override getIdFromLink = (link) => {
    return link.split('/schedules/')[1].split('?')[0];
  };
  buildSchedulesWithCreators(schedules) {
    return this.injectables.$q.all(_.map(schedules, buildCreator));
  }
  createModel(resource, opts?) {
    const model = new ReportScheduleModel(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }
}
